import { useCallback, useRef, useState, useEffect } from "react";
import _ from "lodash";

/**
 * useLongPress
 * Pointer-based hook that distinguishes short click vs long press.
 * Provides isHolding flag for UI feedback during long press delay.
 */

export const useLongPress = (
  onLongPress,
  onClick,
  ms = 1000,
  moveTolerance = 10,
  longPressType,
  onRelease,
  uiHoldDelay = 400 // 👈 new param just for feedback timing
) => {
  const timerRef = useRef(null);
  const uiTimerRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const longTriggeredRef = useRef(false);

  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);

  const start = useCallback((e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;

    const { clientX = 0, clientY = 0 } = e;
    startRef.current = { x: clientX, y: clientY };
    movedRef.current = false;
    longTriggeredRef.current = false;
    setProgress(0);

    // 👇 Start UI feedback after uiHoldDelay (e.g. 300ms)
    clearTimeout(uiTimerRef.current);
    uiTimerRef.current = setTimeout(() => {
      if (!movedRef.current) setIsHolding(true);
    }, uiHoldDelay);

    const startTime = Date.now();
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(elapsed / ms, 1);
      setProgress(pct);
      if (pct >= 1 && !movedRef.current) {
        clearInterval(timerRef.current);
        clearTimeout(uiTimerRef.current);
        setIsHolding(false);
        setProgress(0);
        longTriggeredRef.current = true;
        onLongPress?.(e);
      }
    }, 16);
  }, [onLongPress, ms, uiHoldDelay]);

  const move = useCallback((e) => {
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    if (Math.hypot(dx, dy) > moveTolerance) {
      movedRef.current = true;
      clearInterval(timerRef.current);
      clearTimeout(uiTimerRef.current);
      setIsHolding(false);
      setProgress(0);
    }
  }, [moveTolerance]);

  const end = useCallback((e) => {
    clearInterval(timerRef.current);
    clearTimeout(uiTimerRef.current);
    setIsHolding(false);
    setProgress(0);
    if (longTriggeredRef.current) {
      onRelease?.(e);
      longTriggeredRef.current = false;
    } else if (!movedRef.current) {
      onClick?.(e);
      onRelease?.(e);
    }
  }, [onClick, onRelease]);

  const cancel = useCallback(() => {
    clearInterval(timerRef.current);
    clearTimeout(uiTimerRef.current);
    setIsHolding(false);
    setProgress(0);
    onRelease?.();
  }, [onRelease]);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    clearTimeout(uiTimerRef.current);
  }, []);

  return {
    onPointerDown: start,
    onPointerMove: move,
    onPointerUp: end,
    onPointerCancel: cancel,
    style: { touchAction: "manipulation", userSelect: "none" },
    isHolding,
    progress,
  };
};